import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { ESTADOS } from '~/constant';
import {
  criarCidade,
  atualizarCidade,
  listarRegioesAtendimento,
  listarPaises,
} from '~/Services/Service';

const ESTADOS_OPTIONS = ESTADOS.map((e) => ({ value: e.value, label: e.value + ' — ' + e.label }));

const validationSchema = Yup.object().shape({
  descricao_cidade: Yup.string()
    .required('Descrição é obrigatória')
    .max(30, 'Máximo 30 caracteres'),
  estado: Yup.string().required('Estado é obrigatório'),
  codigo_regiao: Yup.number()
    .typeError('Selecione uma região')
    .required('Selecione uma região')
    .min(1, 'Selecione uma região'),
  codigo_pais: Yup.number()
    .typeError('Selecione um país')
    .required('Selecione um país')
    .min(1, 'Selecione um país'),
  cep_inicial: Yup.string().when('codigo_pais', {
    is: (val) => Number(val) === 1,
    then: Yup.string().test('cep-8', 'CEP deve ter 8 dígitos numéricos', (val) => {
      if (!val) return true;
      return /^\d{8}$/.test(val.replace(/\D/g, ''));
    }),
    otherwise: Yup.string(),
  }),
  cep_final: Yup.string().when('codigo_pais', {
    is: (val) => Number(val) === 1,
    then: Yup.string().test('cep-8', 'CEP deve ter 8 dígitos numéricos', (val) => {
      if (!val) return true;
      return /^\d{8}$/.test(val.replace(/\D/g, ''));
    }),
    otherwise: Yup.string(),
  }),
  latitude: Yup.number()
    .typeError('Latitude deve ser numérica')
    .min(-90, 'Latitude tem que estar entre -90 a 90.')
    .max(90, 'Latitude tem que estar entre -90 a 90.'),
  longitude: Yup.number()
    .typeError('Longitude deve ser numérica')
    .min(-180, 'Longitude tem que estar entre -180 a 180.')
    .max(180, 'Longitude tem que estar entre -180 a 180.'),
  nf_cidade: Yup.string().max(10, 'Máximo 10 caracteres'),
  link_prefeitura: Yup.string().max(300, 'Máximo 300 caracteres'),
});

const getInitialValues = (cidade) => {
  if (cidade) {
    return {
      descricao_cidade: cidade.descricao_cidade || '',
      estado: cidade.estado || 'SP',
      codigo_regiao: cidade.codigo_regiao != null ? cidade.codigo_regiao : '',
      codigo_pais: cidade.codigo_pais != null ? cidade.codigo_pais : 1,
      ddd: cidade.ddd != null ? String(cidade.ddd) : '',
      cep_inicial: cidade.cep_inicial != null ? String(cidade.cep_inicial).padStart(8, '0') : '',
      cep_final: cidade.cep_final != null ? String(cidade.cep_final).padStart(8, '0') : '',
      latitude: cidade.latitude != null ? cidade.latitude : '',
      longitude: cidade.longitude != null ? cidade.longitude : '',
      dimob: cidade.dimob != null ? String(cidade.dimob) : '0',
      nf_cidade: cidade.nf_cidade || '',
      link_prefeitura: cidade.link_prefeitura || '',
    };
  }
  return {
    descricao_cidade: '',
    estado: 'SP',
    codigo_regiao: '',
    codigo_pais: 1,
    ddd: '',
    cep_inicial: '',
    cep_final: '',
    latitude: '',
    longitude: '',
    dimob: '0',
    nf_cidade: '',
    link_prefeitura: '',
  };
};

const calcularCaptulado = (descricao) => {
  if (!descricao) return '';
  return descricao
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .substring(0, 50);
};

const ModalCidade = (props) => {
  const { show, onHide, cidade, onSalvar } = props;
  const ehEdicao = cidade != null;

  const [regioes, setRegioes] = useState([]);
  const [paises, setPaises] = useState([]);

  useEffect(() => {
    if (show) {
      carregarOpcoes();
    }
  }, [show]);

  const carregarOpcoes = async () => {
    try {
      const [reg, pais] = await Promise.all([
        listarRegioesAtendimento(),
        listarPaises(),
      ]);
      setRegioes(reg || []);
      setPaises(pais || []);
    } catch (e) {
      // dropdowns ficam vazios
    }
  };

  const opsRegioes = regioes.map((r) => ({
    value: r.codigo_regiao || r.codigo,
    label: r.descricao_regiao || r.descricao || '',
  }));

  const opsPaises = paises.map((p) => ({
    value: p.codigo_pais || p.codigo,
    label: p.descricao_pais || p.descricao || '',
  }));

  const handleSubmit = async (values, formikBag) => {
    const dto = {
      descricao_cidade: values.descricao_cidade,
      estado: values.estado.toUpperCase(),
      codigo_regiao: Number(values.codigo_regiao),
      codigo_pais: Number(values.codigo_pais),
      ddd: values.ddd !== '' ? Number(values.ddd) : 0,
      cep_inicial: values.cep_inicial !== '' ? Number(values.cep_inicial) : 0,
      cep_final: values.cep_final !== '' ? Number(values.cep_final) : 0,
      latitude: values.latitude !== '' ? Number(values.latitude) : 0,
      longitude: values.longitude !== '' ? Number(values.longitude) : 0,
      dimob: values.dimob !== '' ? Number(values.dimob) : 0,
      nf_cidade: values.nf_cidade || '',
      link_prefeitura: values.link_prefeitura || '',
    };

    try {
      if (ehEdicao) {
        await atualizarCidade(cidade.codigo_cidade, dto);
        toast.success('Cidade atualizada com sucesso.');
      } else {
        await criarCidade(dto);
        toast.success('Cidade criada com sucesso.');
      }
      onSalvar();
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao salvar cidade.';
      toast.error(mensagem);
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          {ehEdicao ? 'Editar Cidade' : 'Nova Cidade'}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={getInitialValues(cidade)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const estadoValue = ESTADOS_OPTIONS.find((o) => o.value === formik.values.estado) || null;
          const regiaoValue = opsRegioes.find((o) => String(o.value) === String(formik.values.codigo_regiao)) || null;
          const paisValue = opsPaises.find((o) => String(o.value) === String(formik.values.codigo_pais)) || null;
          const captulado = calcularCaptulado(formik.values.descricao_cidade);
          const isBrasil = Number(formik.values.codigo_pais) === 1;

          return (
            <Form onSubmit={formik.handleSubmit}>
              <Modal.Body>
                {/* Identificação */}
                <fieldset className="border rounded px-3 pb-2 pt-1 mb-3" style={{ borderColor: '#dee2e6' }}>
                  <legend className="w-auto px-2 mb-0" style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#495057' }}>
                    Identificação
                  </legend>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">Código</Form.Label>
                        <Form.Control
                          size="sm"
                          value={ehEdicao && cidade ? cidade.codigo_cidade : '(auto)'}
                          readOnly
                          disabled
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Descrição <span className="text-danger">*</span>
                        </Form.Label>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="descricao_cidade"
                          size="sm"
                          maxLength={30}
                          style={{ textTransform: 'uppercase' }}
                          isInvalid={formik.touched.descricao_cidade && !!formik.errors.descricao_cidade}
                        />
                        <ErrorMessage name="descricao_cidade" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">Descrição Fiscal (readonly)</Form.Label>
                        <Form.Control
                          size="sm"
                          value={captulado}
                          readOnly
                          disabled
                          style={{ backgroundColor: '#f5f5f5' }}
                          title="Calculado automaticamente pelo servidor"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>

                {/* Localização */}
                <fieldset className="border rounded px-3 pb-2 pt-1 mb-3" style={{ borderColor: '#dee2e6' }}>
                  <legend className="w-auto px-2 mb-0" style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#495057' }}>
                    Localização
                  </legend>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Estado <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={ESTADOS_OPTIONS}
                          value={estadoValue}
                          onChange={(opt) => { formik.setFieldValue('estado', opt ? opt.value : ''); }}
                          styles={selectStylesSm}
                          placeholder="Selecione..."
                          classNamePrefix={formik.touched.estado && formik.errors.estado ? 'select-error' : ''}
                        />
                        {formik.touched.estado && formik.errors.estado && (
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>{formik.errors.estado}</div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          País <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={opsPaises}
                          value={paisValue}
                          onChange={(opt) => { formik.setFieldValue('codigo_pais', opt ? opt.value : ''); }}
                          styles={selectStylesSm}
                          placeholder="Selecione..."
                        />
                        {formik.touched.codigo_pais && formik.errors.codigo_pais && (
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>{formik.errors.codigo_pais}</div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Região <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={opsRegioes}
                          value={regiaoValue}
                          onChange={(opt) => { formik.setFieldValue('codigo_regiao', opt ? opt.value : ''); }}
                          styles={selectStylesSm}
                          placeholder="Selecione..."
                        />
                        {formik.touched.codigo_regiao && formik.errors.codigo_regiao && (
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>{formik.errors.codigo_regiao}</div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">DDD</Form.Label>
                        <Field
                          as={Form.Control}
                          type="number"
                          name="ddd"
                          size="sm"
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>

                {/* CEP */}
                <fieldset className="border rounded px-3 pb-2 pt-1 mb-3" style={{ borderColor: '#dee2e6' }}>
                  <legend className="w-auto px-2 mb-0" style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#495057' }}>
                    CEP {isBrasil && <span className="text-muted" style={{ fontSize: '0.75rem' }}>(8 dígitos obrigatórios para BR)</span>}
                  </legend>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">CEP Inicial</Form.Label>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="cep_inicial"
                          size="sm"
                          maxLength={8}
                          isInvalid={formik.touched.cep_inicial && !!formik.errors.cep_inicial}
                        />
                        <ErrorMessage name="cep_inicial" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">CEP Final</Form.Label>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="cep_final"
                          size="sm"
                          maxLength={8}
                          isInvalid={formik.touched.cep_final && !!formik.errors.cep_final}
                        />
                        <ErrorMessage name="cep_final" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>

                {/* Geolocalização */}
                <fieldset className="border rounded px-3 pb-2 pt-1 mb-3" style={{ borderColor: '#dee2e6' }}>
                  <legend className="w-auto px-2 mb-0" style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#495057' }}>
                    Geolocalização
                  </legend>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">Latitude <span className="text-muted" style={{ fontSize: '0.75rem' }}>(-90 a 90)</span></Form.Label>
                        <Field
                          as={Form.Control}
                          type="number"
                          name="latitude"
                          size="sm"
                          step="0.0001"
                          isInvalid={formik.touched.latitude && !!formik.errors.latitude}
                        />
                        <ErrorMessage name="latitude" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">Longitude <span className="text-muted" style={{ fontSize: '0.75rem' }}>(-180 a 180)</span></Form.Label>
                        <Field
                          as={Form.Control}
                          type="number"
                          name="longitude"
                          size="sm"
                          step="0.0001"
                          isInvalid={formik.touched.longitude && !!formik.errors.longitude}
                        />
                        <ErrorMessage name="longitude" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>

                {/* Fiscal */}
                <fieldset className="border rounded px-3 pb-2 pt-1 mb-2" style={{ borderColor: '#dee2e6' }}>
                  <legend className="w-auto px-2 mb-0" style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#495057' }}>
                    Fiscal
                  </legend>
                  <Row>
                    <Col md={2}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">Dimob</Form.Label>
                        <Field
                          as={Form.Control}
                          type="number"
                          name="dimob"
                          size="sm"
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">NF Cidade</Form.Label>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="nf_cidade"
                          size="sm"
                          maxLength={10}
                          isInvalid={formik.touched.nf_cidade && !!formik.errors.nf_cidade}
                        />
                        <ErrorMessage name="nf_cidade" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                    <Col md={7}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">Link Prefeitura</Form.Label>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="link_prefeitura"
                          size="sm"
                          maxLength={300}
                          isInvalid={formik.touched.link_prefeitura && !!formik.errors.link_prefeitura}
                        />
                        <ErrorMessage name="link_prefeitura" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={formik.isSubmitting}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ModalCidade;
